import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { Button } from 'primereact/button';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { toast } from 'react-toastify';
import i18n from '../../multilingual/config';
import { listAllLanguagesService } from '../../Service/LanguageService';
import {
    createCeremonyTimeService,
    deleteCeremonyTimeService,
    listMyPlaceCeremonyTimesService,
    updateCeremonyTimeService,
} from '../../Service/CeremonyTimeService';

const weekdayValues = [1, 2, 3, 4, 5, 6, 7];

function normalizeTimeForApi(value) {
    if (!value || typeof value !== 'string') return '';
    const trimmed = value.trim();
    if (/^\d{2}:\d{2}$/.test(trimmed)) return `${trimmed}:00`;
    if (/^\d{2}:\d{2}:\d{2}$/.test(trimmed)) return trimmed;
    return '';
}

function toInputTime(value) {
    if (!value || typeof value !== 'string') return '';
    const parts = value.split(':');
    if (parts.length < 2) return '';
    return `${parts[0]}:${parts[1]}`;
}

function labelTime(value) {
    if (!value) return '';
    const normalized = normalizeTimeForApi(value);
    if (!normalized) return '';
    const d = new Date(`1970-01-01T${normalized}`);
    return d.toLocaleTimeString(i18n.language, { hour: 'numeric', minute: '2-digit' });
}

function weekdayLabel(weekday) {
    return moment()
        .day(Number(weekday) - 1)
        .locale(i18n.language === 'pt' ? 'pt-br' : i18n.language === 'es' ? 'es' : 'en')
        .format('dddd');
}

function ReligiousPlaceCeremonyTimesComponent({ user_access_key }) {
    const { t } = useTranslation('user_home');
    const [rows, setRows] = useState([]);
    const [openDialog, setOpenDialog] = useState(false);
    const [editing, setEditing] = useState(null);
    const [weekday, setWeekday] = useState(1);
    const [startTime, setStartTime] = useState('');
    const [finishTime, setFinishTime] = useState('');
    const [languageId, setLanguageId] = useState(null);
    const [languages, setLanguages] = useState([]);
    const [loading, setLoading] = useState(false);

    const sortedRows = useMemo(
        () => [...rows].sort((a, b) => Number(a.weekday) - Number(b.weekday) || String(a.start_time).localeCompare(String(b.start_time))),
        [rows]
    );

    const load = () => {
        if (!user_access_key) return;
        listMyPlaceCeremonyTimesService(user_access_key).then((res) => {
            if (Array.isArray(res)) setRows(res);
            else setRows([]);
        });
    };

    useEffect(() => {
        load();
    }, [user_access_key]);

    useEffect(() => {
        listAllLanguagesService().then((res) => {
            if (Array.isArray(res)) setLanguages(res);
        });
    }, []);

    const resetForm = () => {
        setEditing(null);
        setWeekday(1);
        setStartTime('');
        setFinishTime('');
        setLanguageId(null);
    };

    const openCreate = () => {
        resetForm();
        setOpenDialog(true);
    };

    const openEdit = (item) => {
        setEditing(item);
        setWeekday(Number(item.weekday));
        setStartTime(toInputTime(item.start_time));
        setFinishTime(toInputTime(item.finish_time));
        setLanguageId(item.language_id ?? null);
        setOpenDialog(true);
    };

    const closeDialog = () => {
        setOpenDialog(false);
        resetForm();
    };

    const handleSave = async () => {
        const startForApi = normalizeTimeForApi(startTime);
        const finishForApi = finishTime ? normalizeTimeForApi(finishTime) : null;
        if (!startForApi) {
            toast.error(t('user_home_ceremony_times_validation_start'));
            return;
        }
        if (finishForApi && finishForApi <= startForApi) {
            toast.error(t('user_home_ceremony_times_validation_finish'));
            return;
        }
        setLoading(true);
        const payload = {
            user_access_key,
            weekday,
            start_time: startForApi,
            finish_time: finishForApi,
            language_id: languageId,
        };
        const ok = editing
            ? await updateCeremonyTimeService({ ...payload, old_weekday: editing.weekday, old_start_time: editing.start_time })
            : await createCeremonyTimeService(payload);
        setLoading(false);
        if (ok) {
            toast.success(t(editing ? 'user_home_ceremony_times_saved' : 'user_home_ceremony_times_created'));
            closeDialog();
            load();
        } else {
            toast.error(t('user_home_ceremony_times_error'));
        }
    };

    const handleDelete = (item) => {
        confirmDialog({
            message: `${t('user_home_ceremony_times_delete_message')}${weekdayLabel(item.weekday)} ${labelTime(item.start_time)}?`,
            header: t('user_home_ceremony_times_delete_confirmation'),
            icon: 'pi pi-info-circle',
            defaultFocus: 'reject',
            acceptClassName: 'p-button-danger',
            accept: async () => {
                const ok = await deleteCeremonyTimeService(user_access_key, item.weekday, item.start_time);
                if (ok) {
                    toast.success(t('user_home_ceremony_times_deleted'));
                    load();
                } else {
                    toast.error(t('user_home_ceremony_times_error'));
                }
            },
            reject: () => {},
        });
    };

    return (
        <div className='flex flex-column gap-3'>
            <ConfirmDialog />
            <div className='flex justify-content-between align-items-center'>
                <div><span className='font-bold'>{rows.length}</span> {t('user_home_ceremony_times_count')}</div>
                <Button size='small' icon='pi pi-plus' onClick={openCreate} label={t('user_home_ceremony_times_add')} />
            </div>
            {sortedRows.length === 0 ? (
                <div className='text-600'>{t('user_home_ceremony_times_empty')}</div>
            ) : (
                <div className='flex flex-column gap-2'>
                    {sortedRows.map((item, idx) => (
                        <div key={`${item.weekday}-${item.start_time}-${idx}`} className='flex justify-content-between align-items-center border-1 border-round p-2'>
                            <div className='flex flex-column gap-1'>
                                <span className='font-bold'>{weekdayLabel(item.weekday)}</span>
                                <span>{labelTime(item.start_time)}{item.finish_time ? ` - ${labelTime(item.finish_time)}` : ''}</span>
                                {item.language_id ? <span className='text-sm text-600'>{t('user_home_ceremony_times_language')}: {(languages.find((l) => l.id === item.language_id)?.Language || item.language_id)}</span> : null}
                            </div>
                            <div className='flex gap-2'>
                                <Button icon='pi pi-pencil' onClick={() => openEdit(item)} />
                                <Button icon='pi pi-trash' severity='danger' onClick={() => handleDelete(item)} />
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <Dialog header={editing ? t('user_home_ceremony_times_dialog_edit') : t('user_home_ceremony_times_dialog_new')} visible={openDialog} onHide={closeDialog}>
                <div className='flex flex-column gap-3 min-w-20rem'>
                    <div className='flex flex-column gap-1'>
                        <label>{t('user_home_ceremony_times_weekday')}</label>
                        <select className='p-inputtext p-component' value={weekday} onChange={(e) => setWeekday(Number(e.target.value))}>
                            {weekdayValues.map((value) => (
                                <option key={value} value={value}>{weekdayLabel(value)}</option>
                            ))}
                        </select>
                    </div>
                    <div className='flex flex-column gap-1'>
                        <label>{t('user_home_ceremony_times_start')}</label>
                        <input className='p-inputtext p-component' type='time' value={startTime} onChange={(e) => setStartTime(e.target.value)} />
                    </div>
                    <div className='flex flex-column gap-1'>
                        <label>{t('user_home_ceremony_times_finish')}</label>
                        <input className='p-inputtext p-component' type='time' value={finishTime} onChange={(e) => setFinishTime(e.target.value)} />
                    </div>
                    <div className='flex flex-column gap-1'>
                        <label>{t('user_home_ceremony_times_language')}</label>
                        <Dropdown
                            value={languageId}
                            options={languages}
                            optionLabel='Language'
                            optionValue='id'
                            showClear
                            placeholder={t('user_home_ceremony_times_language_placeholder')}
                            onChange={(e) => setLanguageId(e.value ?? null)}
                        />
                    </div>
                    <div className='flex gap-2 justify-content-end'>
                        <Button text label={t('user_home_ceremony_times_cancel')} onClick={closeDialog} />
                        <Button label={t('user_home_ceremony_times_save')} onClick={handleSave} loading={loading} />
                    </div>
                </div>
            </Dialog>
        </div>
    );
}

export default ReligiousPlaceCeremonyTimesComponent;
